/**
 * سرستونِ قابلِ‌جابه‌جایی/تغییرِ عرض — مشترکِ GxTable و گریدهای دستیِ دیگر.
 *
 * قبلاً فقط داخلِ GxTable بود؛ گریدِ «ثبت کارکرد» جدولِ ساختهٔ خودش است و
 * نمی‌توانست از آن استفاده کند. جدا شد تا یک رفتار (کشیدن = جابه‌جایی،
 * لبه = عرض، دابل‌کلیکِ لبه = اندازهٔ خودکار) همه‌جا یکسان باشد.
 */
import React, { useState } from "react";

/** عرضِ ذاتیِ محتوا (نه عرضِ فعلیِ سلول) — مثلِ auto-fit اکسل.
 *  scrollWidth روی سلولِ پهن = عرض فعلی → هر دابل‌کلیک بزرگ‌تر می‌شد؛
 *  اینجا محتوا را در یک probe خارج از جدول می‌سنجیم. */
export function measureIntrinsicWidth(el) {
  if (!el) return 0;
  const cs = window.getComputedStyle(el);
  const probe = document.createElement("div");
  probe.innerHTML = el.innerHTML;
  // دستگیرهٔ ریسایز و هر چیزِ absolute/مخفی را بردار
  probe.querySelectorAll("*").forEach((n) => {
    const st = (n.getAttribute("style") || "").replace(/\s/g, "");
    if (st.includes("position:absolute") || st.includes("col-resize") || n.getAttribute("aria-hidden") === "true") {
      n.remove();
    }
  });
  const padInline = (parseFloat(cs.paddingInlineStart) || 0) + (parseFloat(cs.paddingInlineEnd) || 0);
  probe.style.cssText = [
    "position:absolute", "visibility:hidden", "pointer-events:none",
    "left:-99999px", "top:0", "height:auto",
    "width:max-content", "max-width:none", "min-width:0",
    "white-space:nowrap", "display:inline-flex", "align-items:center", "gap:4px",
    "box-sizing:border-box",
    `padding-inline:${padInline / 2}px`,
    `font:${cs.font}`, `letter-spacing:${cs.letterSpacing}`,
  ].join(";");
  document.body.appendChild(probe);
  const w = Math.ceil(probe.getBoundingClientRect().width);
  document.body.removeChild(probe);
  return Number.isFinite(w) ? w : 0;
}

// سرستونِ قابلِ‌کشیدن (جابه‌جایی) + دستگیرهٔ تغییرِ عرض + دابل‌کلیک auto-fit.
export function GxHeaderCell({ gxId, gxMove, gxResize, gxAutoFit, children, style, ...rest }) {
  const [over, setOver] = useState(false);
  const [resizing, setResizing] = useState(false);
  const canDrag = !!(gxId && gxMove) && !resizing;

  const startResize = (e) => {
    if (e.button !== 0) return;
    // کلیکِ دومِ دابل‌کلیک → ریسایز شروع نشود (وگرنه با auto-fit تداخل می‌کند)
    if (e.detail > 1) {
      e.preventDefault();
      e.stopPropagation();
      return;
    }
    e.preventDefault();
    e.stopPropagation();
    setResizing(true);
    const th = e.currentTarget.closest("th");
    const startX = e.clientX;
    const startW = th ? th.getBoundingClientRect().width : 120;
    const onMove = (ev) => {
      // دستگیره روی لبهٔ ابتداییِ ستون است؛ کشیدن به سمتِ چپ عرض را زیاد می‌کند.
      gxResize(gxId, Math.max(60, Math.round(startW - (ev.clientX - startX))));
    };
    const onUp = () => {
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup", onUp);
      setResizing(false);
      gxResize(gxId, null, true); // پایان → ذخیره
    };
    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup", onUp);
  };

  return (
    <th
      {...rest}
      data-gx-id={gxId || undefined}
      draggable={canDrag}
      onDragStart={canDrag ? (e) => { e.dataTransfer.effectAllowed = "move"; e.dataTransfer.setData("text/gxcol", gxId); } : undefined}
      onDragOver={canDrag ? (e) => { e.preventDefault(); if (!over) setOver(true); } : undefined}
      onDragLeave={canDrag ? () => setOver(false) : undefined}
      onDragEnd={canDrag ? () => setOver(false) : undefined}
      onDrop={canDrag ? (e) => {
        e.preventDefault();
        setOver(false);
        const from = e.dataTransfer.getData("text/gxcol");
        if (from && from !== gxId) {
          const rAF = window.requestAnimationFrame || ((fn) => setTimeout(fn, 0));
          rAF(() => gxMove(from, gxId));
        }
      } : undefined}
      style={{ ...style, position: "relative", cursor: canDrag ? "grab" : undefined, ...(over ? { boxShadow: "inset 3px 0 0 var(--brand, #1F4E79)" } : null) }}
      title={gxId && gxMove ? "کشیدن = جابه‌جایی؛ لبه = عرض؛ دابل‌کلیکِ لبه = اندازهٔ خودکار" : undefined}
    >
      {children}
      {gxResize && gxId && (
        <span
          key="gx-resize"
          onMouseDown={startResize}
          onDoubleClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            if (typeof gxAutoFit === "function") gxAutoFit(gxId);
          }}
          onClick={(e) => e.stopPropagation()}
          onDragStart={(e) => { e.preventDefault(); e.stopPropagation(); }}
          draggable={false}
          title="بکش = تغییر عرض · دابل‌کلیک = اندازهٔ خودکار (مثل اکسل)"
          style={{ position: "absolute", insetInlineStart: 0, top: 0, height: "100%", width: 8, cursor: "col-resize", userSelect: "none", touchAction: "none", zIndex: 3 }}
        />
      )}
    </th>
  );
}

